// ndt7core is a simple ndt7 client API.
import UploadWorker from 'worker-loader!../workers/ndt7-upload.worker';
import DownloadWorker from 'worker-loader!../workers/ndt7-download.worker';
import {
    ClientResponse,
    DataPublisher,
    LocateResponseResults,
    Measurement,
    ProgressEvent,
    SpeedtestConfig,
    SpeedTestOrigin,
    SpeedtestType,
    WebSocketUrls,
    WebWorkerCallback,
    WebWorkerResponse,
} from '../models';
import { getHostnameForSpeedtestServerNdt7 } from '../locate/locate';

export class NDT7TestCore implements DataPublisher {
    onDownloadSpeedAppNDT7Data(data: ClientResponse): void {}
    onDownloadSpeedNDT7Data(data: Measurement): void {}
    onUploadSpeedAppNDT7Data(data: ClientResponse): void {}
    onUploadSpeedNDT7Data(data: Measurement): void {}
    onFinished(testType: SpeedtestType): void {}
    onTestStarted(testType: SpeedtestType): void {}
    onServerInfo(serverInfo: LocateResponseResults, testType: SpeedtestType): void {}
    private worker;
    private workerUp;
    private done = false;

    public startTest = async (test: SpeedtestType, config: SpeedtestConfig): Promise<void> => {
        const server = await getHostnameForSpeedtestServerNdt7();
        const results = server.results;

        if (test == SpeedtestType.UPLOAD_AND_DOWNLOAD) {
            this.runDownload(results, config, () => this.runUpload(results, config));
        } else if (test == SpeedtestType.UPLOAD) {
            this.runUpload(results, config);
        } else if (test == SpeedtestType.DOWNLOAD) {
            this.runDownload(results, config);
        }
    };

    private runNDT7Test = async (server: WebSocketUrls, test: SpeedtestType, config: SpeedtestConfig, callback: WebWorkerCallback) => {
        callback(ProgressEvent.STARTING);
        this.done = false;
        let didError = false;
        if (test === SpeedtestType.DOWNLOAD) {
            this.worker?.terminate();
            this.worker = undefined;
            this.worker = new DownloadWorker();
            this.worker.onmessage = (msg: Record<string, WebWorkerResponse>) => {
                if (msg.data === null) {
                    this.finish(test, callback);
                    return;
                } else if (msg.data.MsgType === 'error') {
                    didError = true;
                    callback(ProgressEvent.ERROR);
                } else {
                    callback(ProgressEvent.MEASUREMENT, msg.data);
                }
            };

            this.worker.onerror = (_: any) => {
                didError = true;
                callback(ProgressEvent.ERROR);
            }

            this.worker.postMessage({
                href: server,
                config: { maxMessageSizeOverride: config.maxMessageSizeOverride }
            });

            setTimeout(() => {
                this.worker?.terminate();
                if (!didError) {
                    this.finish(test, callback);
                }
            }, 10000);
        } else if (test === SpeedtestType.UPLOAD) {
            this.workerUp?.terminate();
            this.workerUp = undefined;
            this.workerUp = new UploadWorker();
            this.workerUp.onmessage = (msg: Record<string, WebWorkerResponse>) => {
                if (msg.data === null) {
                    this.finish(test, callback);
                    return;
                } else if (msg.data.MsgType === 'error') {
                    didError = true;
                    callback(ProgressEvent.ERROR);
                } else {
                    callback(ProgressEvent.MEASUREMENT, msg.data);
                }
            };

            this.workerUp.postMessage({
                baseURL: server,
                config: { maxMessageSizeOverride: config.maxMessageSizeOverride }
            });

            setTimeout(() => {
                this.workerUp?.terminate();
                if (!didError) {
                    this.finish(test, callback);
                }
            }, 10000);
        }
    };

    private runTest = async (
        testType: SpeedtestType,
        server: LocateResponseResults[],
        currentServerIndex: number,
        config: SpeedtestConfig,
        completionCallback?: () => void,
    ): Promise<void> => {
        const coreCallbacks = this;
        await this.runNDT7Test(server[currentServerIndex].urls, testType, config, (event: ProgressEvent, data?: WebWorkerResponse) => {
            if (event === ProgressEvent.COMPLETE) {
                coreCallbacks.onFinished(testType);
                if (completionCallback) {
                    completionCallback();
                }
                return;
            }

            if (event === ProgressEvent.MEASUREMENT && data) {
                this.onServerInfo(server[currentServerIndex], testType);
                if (data.Source === SpeedTestOrigin.SERVER && testType === SpeedtestType.DOWNLOAD) {
                    coreCallbacks.onDownloadSpeedNDT7Data(data as Measurement);
                }

                if (data.Source === SpeedTestOrigin.CLIENT && testType === SpeedtestType.DOWNLOAD) {
                    coreCallbacks.onDownloadSpeedAppNDT7Data(data as ClientResponse);
                }

                if (data.Source === SpeedTestOrigin.SERVER && testType === SpeedtestType.UPLOAD) {
                    coreCallbacks.onUploadSpeedNDT7Data(data as Measurement);
                }

                if (data.Source === SpeedTestOrigin.CLIENT && testType === SpeedtestType.UPLOAD) {
                    coreCallbacks.onUploadSpeedAppNDT7Data(data as ClientResponse);
                }
            }

            if (event === ProgressEvent.ERROR) {
                config.onErrorCallback?.(`ndt7 ${testType} error`)
                if (currentServerIndex + 1 < server.length) {
                    this.runTest(testType, server, currentServerIndex + 1, config, completionCallback);
                } else {
                    if (completionCallback) {
                        completionCallback();
                    }
                    return;
                }
            }
        });
    };

    private runDownload = (server: LocateResponseResults[], config: SpeedtestConfig, completionCallback?: () => void) => {
        this.onTestStarted(SpeedtestType.DOWNLOAD);
        this.runTest(SpeedtestType.DOWNLOAD, server, 0, config, completionCallback);
    };

    private runUpload = (server: LocateResponseResults[], config: SpeedtestConfig, completionCallback?: () => void) => {
        this.onTestStarted(SpeedtestType.UPLOAD);
        this.runTest(SpeedtestType.UPLOAD, server, 0, config, completionCallback);
    };

    public cancelNDT7Test = () => {
        if (this.worker != null) {
            this.worker.terminate();
            this.worker.onmessage = null;
        }
        this.done = true;
    };

    private finish = (testType: SpeedtestType, callback: WebWorkerCallback) => {
        if (!this.done) {
            this.done = true;
            callback(ProgressEvent.COMPLETE);
        }
    };
}
